'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var Vector = require('./Vector-02aac05d.cjs.prod.js');
var React = require('react');
var ReactDOM = require('react-dom');
var create = require('zustand');
var shallow = require('zustand/shallow');
var vectorPlugin = require('./vector-plugin-dc78d9b0.cjs.prod.js');
var merge = require('merge-value');
var dequal = require('dequal');
var reactUseGesture = require('react-use-gesture');
var v8n = require('v8n');
require('clipboard-polyfill/text');
var tc = require('tinycolor2');
var reactColorful = require('react-colorful');
require('react-colorful/dist/index.css');
var reactDropzone = require('react-dropzone');
require('@stitches/react');

function _interopDefault (e) { return e && e.__esModule ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefault(React);
var ReactDOM__default = /*#__PURE__*/_interopDefault(ReactDOM);
var create__default = /*#__PURE__*/_interopDefault(create);
var shallow__default = /*#__PURE__*/_interopDefault(shallow);
var merge__default = /*#__PURE__*/_interopDefault(merge);
var v8n__default = /*#__PURE__*/_interopDefault(v8n);
var tc__default = /*#__PURE__*/_interopDefault(tc);

const join = (...args) => args.filter(Boolean).join('.');
function getKeyPath(path) {
  const dir = path.split('.');
  return [dir.pop(), dir.join('.') || undefined];
}

// zustand store
const _store = create__default['default'](() => ({
  data: {}
})); // possibly make this reactive


const FOLDERS = {};
const PATHS = new Set();
const useStore = _store; // shorthand to get zustand store data

const getData = () => _store.getState().data;

function setPaths(newPaths) {
  newPaths.forEach(p => PATHS.add(p));
}
/**
 * Merges the data passed as an argument with the store data.
 * If an input path from the data already exists in the store,
 * the function doesn't update the data but increments count
 * to keep track of how many components use that input key.
 * @param newData the data to update
 */


function setData(newData) {
  _store.setState(s => {
    const data = s.data;
    Object.entries(newData).forEach(([path, value]) => {
      const input = data[path]; // if an input already exists at the path, increment
      // the reference count.

      if (!!input) input.count++; // if not, create a path for the input.
      else data[path] = { ...value,
          count: 1
        };
    }); // TODO not sure about direct mutation but since this
    // returns a new object that should work and trigger
    // a re-render.

    return {
      data
    };
  });
}
/**
 * Shorthand function to set the value of an input at a given path.
 * @param path path of the input
 * @param value new value of the input
 */


function setValueAtPath(path, value) {
  _store.setState(s => {
    const data = s.data; //@ts-expect-error (we always update inputs with a value)

    Vector.updateInput(data[path], value);
    return {
      data
    };
  });
}

function getValueAtPath(path) {
  return _store.getState().data[path];
}
/**
 * For a given data structure, gets all paths for which inputs have
 * a reference count superior to zero. This function is used by the
 * root pane to only display the inputs that are consumed by mounted
 * components.
 * @param data
 */


function getVisiblePaths(data) {
  const visiblePaths = [];
  PATHS.forEach(path => {
    if (data[path]?.count > 0) visiblePaths.push(path);
  });
  return visiblePaths;
}
/**
 * Hook used by the root component to get all visible inputs.
 */


const useVisiblePaths = () => useStore(s => getVisiblePaths(s.data), shallow__default['default']);

function getValuesForPaths(data, paths, shouldWarn) {
  return Object.entries(vectorPlugin.pick(data, paths)).reduce( // Typescript complaints that SpecialInput type doesn't have a value key.
  // But getValuesForPath is only called from paths that are inputs,
  // so they always have a value key.
  // @ts-expect-error
  (acc, [path, {
    value
  }]) => {
    const [key] = getKeyPath(path); // if a key already exists in the accumulator, prompt an error.

    if (acc[key] !== undefined) {
      if (shouldWarn) vectorPlugin.warn(vectorPlugin.LevaErrors.DUPLICATE_KEYS, key, path);
      return acc;
    }

    return { ...acc,
      [key]: value
    };
  }, {});
}
/**
 * Hook that returns the values from the zustand store for the given paths.
 * @param paths paths for which to return values
 * @param initialData
 */


function useValuesForPath(paths, initialData) {
  // init is used to know when to prompt duplicate key errors to the user.
  // We don't want to show the errors on every render, only when the hook
  // is first used!
  const init = React.useRef(true);
  const valuesForPath = useStore(s => {
    const data = init.current ? initialData : s.data;
    return getValuesForPaths(data, paths, init.current);
  }, shallow__default['default']);
  init.current = false;
  return valuesForPath;
}
/**
 * Return all input (value and settings) properties at a given path.
 * @param path
 */

function useInput(path) {
  return useStore(s => {
    const {
      count,
      ...input
    } = s.data[path];
    return input;
  }, shallow__default['default']);
}
const getFolderSettings = path => FOLDERS[path];
/**
 * Extract the data from the schema and sets folder initial preferences.
 * @param schema
 */

function getDataFromSchema(schema, rootPath = '') {
  const data = {};
  Object.entries(schema).forEach(([path, input]) => {
    const newPath = join(rootPath, path);

    if (input.type === vectorPlugin.SpecialInputTypes.FOLDER) {
      Object.assign(data, getDataFromSchema(input.schema, newPath));
      FOLDERS[newPath] = input.settings;
    } else {
      const normalizedInput = Vector.normalizeInput(input, newPath);

      if (normalizedInput) {
        data[newPath] = normalizedInput;
      }
    }
  });
  return data;
}
function getPaths(initialData) {
  const paths = Object.keys(initialData);
  setPaths(paths);
  return paths;
}

function disposePaths(paths) {
  _store.setState(s => {
    const data = s.data;
    paths.forEach(path => data[path].count--);
    return {
      data
    };
  });
}

const store = {
  getData,
  setData,
  setValueAtPath,
  getValueAtPath,
  disposePaths
};

// @ts-expect-error
const buildTree = (paths, filter) => {
  const tree = {};

  const _filter = filter ? filter.toLowerCase() : null;

  paths.forEach(path => {
    const [key, folderPath] = getKeyPath(path);

    if (!_filter || key.toLowerCase().indexOf(_filter) > -1) {
      merge__default['default'](tree, folderPath, {
        [key]: {
          __levaInput: true,
          path,
          valueKey: key
        }
      });
    }
  });
  return tree;
};

// TODO should we rather use something like use-onclickoutside?
function Overlay({
  onClick
}) {
  return /*#__PURE__*/React__default['default'].createElement("div", {
    style: {
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0
    },
    onClick: onClick
  });
}

function useDeepCompareMemoize(value) {
  const ref = React.useRef();

  if (!dequal.dequal(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepMemo(fn, deps) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(fn, useDeepCompareMemoize(deps));
}

function useTransform() {
  const ref = React.useRef(null);
  const local = React.useRef({
    x: 0,
    y: 0
  });
  const set = React.useCallback(point => {
    Object.assign(local.current, point);
    if (ref.current) ref.current.style.transform = `translate3d(${local.current.x}px, ${local.current.y}px, 0)`;
  }, []);
  return [ref, set];
}

function FolderTitle({
  toggle,
  toggled,
  name
}) {
  return /*#__PURE__*/React__default['default'].createElement(Vector.StyledTitle, {
    onClick: () => toggle()
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Chevron, {
    toggled: toggled
  }), /*#__PURE__*/React__default['default'].createElement("div", null, name));
}

function LevaValueInput({
  as: Input,
  valueKey,
  path,
  type,
  value,
  settings
}) {
  const set = React.useCallback(value => store.setValueAtPath(path, value), [path]);
  const {
    displayValue,
    onChange,
    onUpdate
  } = Vector.useLevaUpdate({
    type,
    value,
    settings,
    set
  });
  return /*#__PURE__*/React__default['default'].createElement(Vector.InputContext.Provider, {
    value: {
      valueKey,
      label: valueKey,
      displayValue,
      value,
      onChange,
      onUpdate,
      settings
    }
  }, /*#__PURE__*/React__default['default'].createElement(Input, null));
}

const StyledButton = Vector.styled('button', {
  display: 'block',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  color: '$textEmphasized',
  appearance: 'none',
  height: '$rowHeight',
  borderStyle: 'none',
  borderRadius: '$sm',
  outline: 'none',
  backgroundColor: '$accent',
  transition: '$borderBg',
  cursor: 'pointer',
  '&:hover': {
    border: '$input solid $accent3'
  },
  '&:active': {
    border: '$input solid $accent3',
    backgroundColor: '$accent2'
  }
});

function Button({
  onClick,
  valueKey
}) {
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, null, /*#__PURE__*/React__default['default'].createElement(StyledButton, {
    onClick: () => onClick()
  }, valueKey));
}

const Canvas = Vector.styled('canvas', {
  height: '60px',
  width: '100%',
  display: 'block',
  backgroundColor: '$elevation3',
  borderRadius: '$sm'
});

const POINTS = 100;

function push(arr, val) {
  arr.push(val);
  if (arr.length > POINTS) arr.shift();
}

const MonitorCanvas = /*#__PURE__*/React.forwardRef(function ({
  initialValue
}, ref) {
  const accentColor = Vector.useTh('colors', '$textEmphasized');
  const fillColor = Vector.useTh('colors', '$elevation1');
  const points = React.useRef([initialValue]);
  const min = React.useRef(initialValue);
  const max = React.useRef(initialValue);
  const raf = React.useRef();
  const drawPlot = React.useCallback((_canvas, _ctx) => {
    // fixes unmount potential bug
    if (!_canvas) return;
    const {
      width,
      height
    } = _canvas;

    _ctx.clearRect(0, 0, width, height);

    _ctx.beginPath();

    _ctx.strokeStyle = accentColor;
    _ctx.fillStyle = fillColor;
    _ctx.lineWidth = 2;
    const interval = width / POINTS;

    for (let i = 0; i < points.current.length; i++) {
      const p = vectorPlugin.range(points.current[i], min.current, max.current);

      _ctx.lineTo(interval * i, p * height * 0.9);
    }

    _ctx.stroke();

    _ctx.lineTo(interval * (points.current.length + 1), height);

    _ctx.lineTo(0, height);

    _ctx.lineTo(0, 0);

    _ctx.fill();
  }, [accentColor, fillColor]);
  const [canvas, ctx] = Vector.useCanvas2d(drawPlot);
  React.useImperativeHandle(ref, () => ({
    frame: val => {
      if (min.current === undefined || val < min.current) min.current = val;
      if (max.current === undefined || val > max.current) max.current = val;
      push(points.current, val);
      raf.current = requestAnimationFrame(() => drawPlot(canvas.current, ctx.current));
    }
  }), [canvas, ctx, drawPlot]);
  React.useEffect(() => () => cancelAnimationFrame(raf.current), []);
  return /*#__PURE__*/React__default['default'].createElement(Canvas, {
    ref: canvas
  });
});

const parse = val => Number.isFinite(val) ? val.toPrecision(2) : val.toString();

const MonitorLog = /*#__PURE__*/React.forwardRef(function ({
  initialValue
}, ref) {
  const [val, set] = React.useState(parse(initialValue));
  React.useImperativeHandle(ref, () => ({
    frame: v => set(parse(v))
  }), []);
  return /*#__PURE__*/React__default['default'].createElement("div", null, val);
});

function getValue(o) {
  return typeof o === 'function' ? o() : o.current;
}

function Monitor({
  valueKey,
  objectOrFn,
  settings
}) {
  const ref = React.useRef();
  const initialValue = React.useRef(getValue(objectOrFn));
  React.useEffect(() => {
    const timeout = window.setInterval(() => ref.current?.frame(getValue(objectOrFn)), settings.interval);
    return () => window.clearInterval(timeout);
  }, [objectOrFn, settings.interval]);
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, valueKey), settings.graph ? /*#__PURE__*/React__default['default'].createElement(MonitorCanvas, {
    ref: ref,
    initialValue: initialValue.current
  }) : /*#__PURE__*/React__default['default'].createElement(MonitorLog, {
    ref: ref,
    initialValue: initialValue.current
  }));
}

const specialComponents = {
  [vectorPlugin.SpecialInputTypes.BUTTON]: Button,
  [vectorPlugin.SpecialInputTypes.MONITOR]: Monitor
}; // TODO we can probably do better than this

function LevaWrapper({
  valueKey,
  path
}) {
  const {
    type,
    ...props
  } = useInput(path);

  if (type in vectorPlugin.SpecialInputTypes) {
    // @ts-expect-error
    const SpecialInputForType = specialComponents[type];
    return /*#__PURE__*/React__default['default'].createElement(SpecialInputForType, Vector._extends({
      valueKey: valueKey,
      path: path
    }, props));
  }

  if (!(type in Vector.Plugins)) {
    vectorPlugin.log(vectorPlugin.LevaErrors.UNSUPPORTED_INPUT, type, path);
    return null;
  }

  const Input = Vector.Plugins[type].component; // @ts-expect-error

  return /*#__PURE__*/React__default['default'].createElement(LevaValueInput, Vector._extends({
    as: Input,
    type: type,
    valueKey: valueKey,
    path: path
  }, props));
}

const createFolder = (key, parent = '', tree) => {
  const path = join(parent, key);
  const settings = getFolderSettings(path);
  return /*#__PURE__*/React__default['default'].createElement(Folder, Vector._extends({
    key: key,
    name: key,
    parent: path,
    tree: tree
  }, settings));
};

const Folder = /*#__PURE__*/React__default['default'].memo(({
  name,
  parent,
  tree,
  isRoot = false,
  collapsed = false,
  TitleComponent
}) => {
  const contentRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const firstRender = React.useRef(true);
  const [toggled, setToggle] = React.useState(!collapsed);
  const toggle = React.useCallback(() => setToggle(t => !t), []);
  const Title = React.useMemo(() => TitleComponent ? TitleComponent({
    name,
    toggle,
    toggled
  }) : /*#__PURE__*/React__default['default'].createElement(FolderTitle, {
    name: name,
    toggled: toggled,
    toggle: toggle
  }), [TitleComponent, name, toggle, toggled]);
  React.useEffect(() => {
    // prevents first animation
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    let timeout;
    const ref = wrapperRef.current;

    const fixHeight = () => toggled && ref.style.removeProperty('height');

    ref.addEventListener('transitionend', fixHeight, {
      once: true
    });
    const {
      height
    } = contentRef.current.getBoundingClientRect();
    if (toggled) ref.style.height = height + 'px';else {
      ref.style.height = height + 'px';
      timeout = window.setTimeout(() => ref.style.height = '0px', 50);
    }
    return () => {
      ref.removeEventListener('transitionend', fixHeight);
      clearTimeout(timeout);
    };
  }, [toggled]);
  return /*#__PURE__*/React__default['default'].createElement(Vector.StyledFolder, {
    isRoot: isRoot
  }, Title, /*#__PURE__*/React__default['default'].createElement(Vector.StyledWrapper, {
    ref: wrapperRef,
    isRoot: isRoot,
    toggled: toggled
  }, /*#__PURE__*/React__default['default'].createElement(Vector.StyledContent, {
    ref: contentRef,
    isRoot: isRoot,
    toggled: toggled
  }, Object.entries(tree).map(([key, value]) => Vector.isInput(value) ?
  /*#__PURE__*/
  // @ts-expect-error
  React__default['default'].createElement(LevaWrapper, {
    key: value.path,
    valueKey: value.valueKey,
    path: value.path
  }) : createFolder(key, parent, value)))));
});

Vector.styled(Vector.StyledTitle, {
  position: 'absolute',
  width: '100%',
  opacity: 0,
  touchAction: 'none'
});
const StyledTitleWithFilter = Vector.styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '43px',
  cursor: 'grab'
});
const FilterWrapper = Vector.styled('div', {
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  transition: 'height 250ms ease',
  variants: {
    toggled: {
      true: {
        height: 30
      },
      false: {
        height: 0
      }
    }
  }
});
const StyledFilterInput = Vector.styled('input', {
  height: 30,
  width: '100%',
  padding: '0 $rowH',
  backgroundColor: 'transparent',
  transition: '$bg',
  border: 'none',
  outline: 'none',
  color: '$textEmphasized',
  fontFamily: 'inherit',
  fontSize: '10px',
  borderRadius: '$root',
  '&:focus': {},
  '::placeholder': {
    color: '$text'
  }
});
const Icon = Vector.styled('i', {
  width: 40,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  cursor: 'pointer',
  '> svg': {
    fill: '$textDeEmphasized',
    transition: 'transform 350ms ease, fill 250ms ease'
  },
  ':hover > svg': {
    fill: '$textEmphasized'
  },
  variants: {
    active: {
      true: {
        '> svg': {
          fill: '$text'
        }
      }
    }
  }
});
const Drag = Vector.styled('div', {
  display: 'flex',
  justifyContent: 'center',
  flex: 1,
  '> svg': {
    fill: '$textDeEmphasized',
    transition: 'fill 250ms ease'
  },
  ':hover > svg': {
    fill: '$textEmphasized'
  }
});

const FilterInput = /*#__PURE__*/React__default['default'].forwardRef(({
  setFilter
}, ref) => {
  const [value, set] = React.useState('');
  const debouncedOnChange = React.useMemo(() => vectorPlugin.debounce(setFilter, 250), [setFilter]);

  const _onChange = e => {
    const v = e.currentTarget.value;
    set(v);
    debouncedOnChange(v);
  };

  return /*#__PURE__*/React__default['default'].createElement(StyledFilterInput, {
    ref: ref,
    value: value,
    placeholder: "[Open this filter field with CMD+SHIFT+L]",
    onPointerDown: e => e.stopPropagation(),
    onChange: _onChange
  });
});
function TitleWithFilter({
  setFilter,
  onDrag,
  toggle,
  toggled
}) {
  const [filterShown, setShowFilter] = React.useState(false);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (filterShown) inputRef.current?.focus();else inputRef.current?.blur();
  }, [filterShown]);
  const bind = reactUseGesture.useDrag(({
    offset: [x, y]
  }) => onDrag({
    x,
    y
  }), {
    filterTaps: true
  });
  React.useEffect(() => {
    const handleShortcut = event => {
      if (event.key === 'L' && event.shiftKey && event.metaKey) {
        setShowFilter(f => !f);
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);
  return /*#__PURE__*/React__default['default'].createElement(React__default['default'].Fragment, null, /*#__PURE__*/React__default['default'].createElement(StyledTitleWithFilter, bind(), /*#__PURE__*/React__default['default'].createElement(Icon, {
    active: !toggled,
    onClick: () => toggle()
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Chevron, {
    toggled: toggled,
    width: 12,
    height: 8
  })), /*#__PURE__*/React__default['default'].createElement(Drag, null, /*#__PURE__*/React__default['default'].createElement("svg", {
    width: "20",
    height: "10",
    viewBox: "0 0 28 14",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default['default'].createElement("circle", {
    cx: "2",
    cy: "2",
    r: "2"
  }), /*#__PURE__*/React__default['default'].createElement("circle", {
    cx: "14",
    cy: "2",
    r: "2"
  }), /*#__PURE__*/React__default['default'].createElement("circle", {
    cx: "26",
    cy: "2",
    r: "2"
  }), /*#__PURE__*/React__default['default'].createElement("circle", {
    cx: "2",
    cy: "12",
    r: "2"
  }), /*#__PURE__*/React__default['default'].createElement("circle", {
    cx: "14",
    cy: "12",
    r: "2"
  }), /*#__PURE__*/React__default['default'].createElement("circle", {
    cx: "26",
    cy: "12",
    r: "2"
  }))), /*#__PURE__*/React__default['default'].createElement(Icon, {
    active: filterShown,
    onClick: () => setShowFilter(f => !f)
  }, /*#__PURE__*/React__default['default'].createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    height: "20",
    viewBox: "0 0 20 20"
  }, /*#__PURE__*/React__default['default'].createElement("path", {
    d: "M9 9a2 2 0 114 0 2 2 0 01-4 0z"
  }), /*#__PURE__*/React__default['default'].createElement("path", {
    fillRule: "evenodd",
    d: "M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a4 4 0 00-3.446 6.032l-2.261 2.26a1 1 0 101.414 1.415l2.261-2.261A4 4 0 1011 5z",
    clipRule: "evenodd"
  })))), /*#__PURE__*/React__default['default'].createElement(FilterWrapper, {
    toggled: filterShown
  }, /*#__PURE__*/React__default['default'].createElement(FilterInput, {
    ref: inputRef,
    setFilter: setFilter
  })));
}

const Root = Vector.styled('div', {
  /* position */
  position: 'relative',
  fontFamily: '$mono',
  fontSize: '$root',
  backgroundColor: '$elevation1',
  boxShadow: '$root',
  variants: {
    fillParent: {
      false: {
        position: 'fixed',
        top: '10px',
        right: '10px',
        color: '$rootText',
        width: '$rootWidth',
        borderRadius: '$lg',
        zIndex: 1000
      },
      true: {
        position: 'relative',
        width: '100%'
      }
    }
  },
  '&,*,*:after,*:before': {
    boxSizing: 'border-box'
  },
  '*::selection': {
    backgroundColor: '$accent'
  }
});

let rootInitialized = false;
function Leva({
  theme = {},
  fillParent = false,
  collapsed = false
}) {
  // data
  const paths = useVisiblePaths();
  const [filter, setFilter] = React.useState('');
  const tree = React.useMemo(() => buildTree(paths, filter), [paths, filter]); // theme

  Vector.globalStyles();
  const {
    mergedTheme,
    themeCss
  } = useDeepMemo(() => Vector.mergeTheme(theme), [theme]); // drag

  const [rootRef, set] = useTransform(); // TODO check if using useEffect is the right hook (we used useLayoutEffect before)

  React.useEffect(() => {
    rootInitialized = true;
  }, []);
  if (paths.length < 1) return null;
  /**
   * @todo remove
   * we know there's a folder at the root of the root if the first
   * key isn't an input. isFolderOnTop is used to show an dummy folder at
   * the top of the pane.
   */
  // const values = Object.values(tree)
  // const isFolderOnTop = values.length > 0 && !isInput(values[0])

  return /*#__PURE__*/React__default['default'].createElement(Vector.ThemeContext.Provider, {
    value: mergedTheme
  }, /*#__PURE__*/React__default['default'].createElement(Root, {
    ref: rootRef,
    className: themeCss,
    fillParent: fillParent
  }, /*#__PURE__*/React__default['default'].createElement(Folder, {
    isRoot: true,
    name: "Leva",
    tree: tree,
    collapsed: collapsed,
    TitleComponent: props => /*#__PURE__*/React__default['default'].createElement(TitleWithFilter, Vector._extends({
      onDrag: set,
      setFilter: setFilter
    }, props))
  })));
}
function useRenderRoot() {
  React.useEffect(() => {
    if (!rootInitialized) {
      const rootEl = document.createElement('div');

      if (document.body) {
        document.body.appendChild(rootEl);
        ReactDOM__default['default'].render( /*#__PURE__*/React__default['default'].createElement(Leva, null), rootEl);
      }

      rootInitialized = true;
    }
  }, []);
}

const defaultSettings = {
  collapsed: false
};
function folder(schema, settings) {
  return {
    type: vectorPlugin.SpecialInputTypes.FOLDER,
    schema,
    settings: { ...defaultSettings,
      ...settings
    }
  };
}

const {
  sanitizeStep,
  ...rest
} = vectorPlugin.props;
const plugin = { ...rest,
  component: Vector.Number
};

/*
export type ValueTypeFromInput<T> = T extends SelectInput<infer P, infer U> ? U | P : never
const d = { value: {}, options: { a: 'allo', b: true } }
let c: ValueTypeFromInput<typeof d>
*/
// the options attribute is either an key value object or an array
const schema = (_o, s) => v8n__default['default']().schema({
  options: v8n__default['default']().passesAnyOf(v8n__default['default']().object(), v8n__default['default']().array())
}).test(s);
const format = (value, {
  values
}) => {
  return values.indexOf(value);
};
const normalize = input => {
  let {
    value,
    options
  } = input;
  let keys;
  let values;

  if (Array.isArray(options)) {
    values = options;
    keys = options.map(String);
  } else {
    values = Object.values(options);
    keys = Object.keys(options);
  }

  if (!('value' in input)) value = values[0];else if (!values.includes(value)) {
    keys.unshift(String(value));
    values.unshift(value);
  }
  if (!Object.values(options).includes(value)) options[String(value)] = value;
  return {
    value,
    settings: {
      keys,
      values
    }
  };
};

var props = /*#__PURE__*/Object.freeze({
  __proto__: null,
  schema: schema,
  format: format,
  normalize: normalize
});

const SelectContainer = Vector.styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '> svg': {
    pointerEvents: 'none',
    position: 'absolute',
    right: '$rowH'
  }
});
const StyledSelect = Vector.styled('select', {
  width: '100%',
  appearance: 'none',
  outline: 'none',
  display: 'block',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  height: '$rowHeight',
  backgroundColor: '$elevation3',
  color: '$textEmphasized',
  borderStyle: 'none',
  borderRadius: '$sm',
  padding: '0 $input',
  cursor: 'pointer',
  transition: 'border',
  '&:hover': {
    borderColor: '$inputHoverBorder'
  }
});

function Select() {
  const {
    label,
    displayValue,
    onUpdate,
    settings
  } = Vector.useInputContext();
  const {
    keys,
    values
  } = settings;
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(SelectContainer, null, /*#__PURE__*/React__default['default'].createElement(StyledSelect, {
    value: displayValue,
    onChange: e => onUpdate(values[+e.currentTarget.value])
  }, keys.map((key, index) => /*#__PURE__*/React__default['default'].createElement("option", {
    key: key,
    value: index
  }, key))), /*#__PURE__*/React__default['default'].createElement(Vector.Chevron, {
    toggled: true
  })));
}

const plugin$1 = { ...props,
  component: Select
};

const FORMATS = ['name', 'hex', 'hex8', 'rgb'];
v8n__default['default'].extend({
  color: () => value => FORMATS.includes(tc__default['default'](value).getFormat())
}); // prettier-ignore
// @ts-expect-error

const schema$1 = o => v8n__default['default']().color().test(o);

function convert(color, {
  format,
  hasAlpha
}) {
  if (format === 'hex') return color[hasAlpha ? 'toHex8String' : 'toHexString']();
  const rgba = color.toRgb();
  return hasAlpha ? rgba : vectorPlugin.pick(rgba, ['r', 'g', 'b']);
}

const validate = v => tc__default['default'](v).isValid();
const sanitize = (v, settings) => convert(tc__default['default'](v), settings);
const format$1 = (v, {
  hasAlpha
}) => convert(tc__default['default'](v), {
  format: 'hex',
  hasAlpha
});
const normalize$1 = ({
  value
}) => {
  const color = tc__default['default'](value);

  const _f = color.getFormat();

  const format = _f === 'name' || _f === 'hex8' ? 'hex' : _f;
  const hasAlpha = format === 'rgb' ? 'a' in value : _f === 'hex8';
  return {
    value,
    settings: {
      format,
      hasAlpha
    }
  };
};

var props$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  schema: schema$1,
  validate: validate,
  sanitize: sanitize,
  format: format$1,
  normalize: normalize$1
});

const ColorPreview = Vector.styled('div', {
  boxSizing: 'border-box',
  borderRadius: '$sm',
  cursor: 'pointer',
  height: '$rowHeight',
  width: '$rowHeight',
  border: '$input solid $elevation3',
  '&:hover': {},
  '&:active': {
    borderColor: '$accent'
  }
});
const PickerContainer = Vector.styled('div', {
  position: 'relative',
  display: 'grid',
  // Stitches creates human readable css vars 🤟
  gridTemplateColumns: 'var(--sizes-rowHeight) auto',
  gridColumnGap: '$colGap',
  alignItems: 'center',
  '& > span': {
    fontSize: '0.95em',
    opacity: '0.8',
    padding: '0 $rowH'
  }
});
const PickerWrapper = Vector.styled('div', {
  position: 'absolute',
  bottom: 0,
  left: 0,
  zIndex: 100,
  '.react-colorful': {
    width: '100px',
    height: '100px',
    transform: 'translateY(100%)',
    boxShadow: '$overlay',
    cursor: 'crosshair'
  },
  '.react-colorful__saturation': {
    borderRadius: '$sm $sm 0 0'
  },
  '.react-colorful__alpha, .react-colorful__hue': {
    height: '10px'
  },
  '.react-colorful__last-control': {
    borderRadius: '0 0 $sm $sm'
  },
  '.react-colorful__pointer': {
    height: '12px',
    width: '12px'
  }
});

function Color() {
  const {
    value,
    displayValue,
    label,
    onChange,
    onUpdate,
    settings
  } = Vector.useInputContext();
  const [showPicker, setShowPicker] = React.useState(false);
  const {
    format,
    hasAlpha
  } = settings;
  const rgb = format !== 'rgb' ? tc__default['default'](value).toRgb() : value;
  const ColorPicker = hasAlpha ? reactColorful.RgbaColorPicker : reactColorful.RgbColorPicker;
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(PickerContainer, null, /*#__PURE__*/React__default['default'].createElement(ColorPreview, {
    onClick: () => setShowPicker(true),
    style: {
      background: displayValue
    }
  }), /*#__PURE__*/React__default['default'].createElement(Vector.ValueInput, {
    value: displayValue,
    onChange: onChange,
    onUpdate: onUpdate
  }), showPicker && /*#__PURE__*/React__default['default'].createElement(PickerWrapper, null, /*#__PURE__*/React__default['default'].createElement(Overlay, {
    onClick: () => setShowPicker(false)
  }), /*#__PURE__*/React__default['default'].createElement(ColorPicker, {
    color: rgb,
    onChange: onUpdate
  }))));
}

const plugin$2 = { ...props$1,
  component: Color
};

const schema$2 = o => v8n__default['default']().string().test(o);

var props$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  schema: schema$2
});

function String$1() {
  const {
    label,
    displayValue,
    onUpdate,
    onChange
  } = Vector.useInputContext();
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(Vector.ValueInput, {
    value: displayValue,
    onUpdate: onUpdate,
    onChange: onChange
  }));
}

const plugin$3 = { ...props$2,
  component: String$1
};

const schema$3 = o => v8n__default['default']().boolean().test(o);

var props$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  schema: schema$3
});

const StyledInputWrapper = Vector.styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  height: '$rowHeight',
  input: {
    height: 0,
    width: 0,
    opactiy: 0,
    margin: 0
  },
  label: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    height: 'calc(var(--sizes-rowHeight) - 8px)',
    width: 'calc(var(--sizes-rowHeight) - 8px)',
    backgroundColor: '$elevation3',
    borderRadius: '$sm'
  },
  'input:focus + label': {
    border: '$input solid $accent'
  },
  'label:active': {
    backgroundColor: '$accent2'
  },
  'label > svg': {
    display: 'none',
    width: '90%',
    height: '90%',
    stroke: '$textEmphasized'
  },
  'input:checked + label': {
    backgroundColor: '$accent'
  },
  'input:checked + label > svg': {
    display: 'block'
  }
});

function Boolean$1() {
  const {
    label,
    value,
    onUpdate
  } = Vector.useInputContext();
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(StyledInputWrapper, null, /*#__PURE__*/React__default['default'].createElement("input", {
    id: label,
    type: "checkbox",
    checked: value,
    onChange: e => onUpdate(e.currentTarget.checked)
  }), /*#__PURE__*/React__default['default'].createElement("label", {
    htmlFor: label
  }, /*#__PURE__*/React__default['default'].createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React__default['default'].createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M5 13l4 4L19 7"
  })))));
}

const plugin$4 = { ...props$3,
  component: Boolean$1
};

const Container = Vector.styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridColumnGap: '$colGap'
});
function Point3d() {
  const {
    label,
    displayValue,
    onUpdate,
    settings
  } = Vector.useInputContext();
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(Container, null, /*#__PURE__*/React__default['default'].createElement(Vector.Vector, {
    value: displayValue,
    settings: settings,
    onUpdate: onUpdate
  })));
}

const KEYS = ['x', 'y', 'z'];

const plugin$5 = { ...vectorPlugin.getVectorPlugin(KEYS),
  component: Point3d
};

const JoystickTrigger = Vector.styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  backgroundColor: '$elevation3',
  borderRadius: '$sm',
  cursor: 'pointer',
  height: '$rowHeight',
  width: '$rowHeight',
  touchAction: 'none',
  ':hover': {},
  ':active': {
    cursor: 'none'
  },
  '::after': {
    content: '""',
    backgroundColor: '$accent',
    height: '4px',
    width: '4px',
    borderRadius: '2px'
  }
});
const JoystickPlayground = Vector.styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '$joystickWidth',
  height: '$joystickHeight',
  borderRadius: '$sm',
  boxShadow: '$overlay',
  position: 'absolute',
  zIndex: 100,
  overflow: 'hidden',
  transition: '$bg',
  variants: {
    isOutOfBounds: {
      true: {
        backgroundColor: '$elevation1'
      },
      false: {
        backgroundColor: '$elevation3'
      }
    }
  },
  '> div': {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: '$textDeEmphasized',
    backgroundColor: '$elevation3',
    width: '80%',
    height: '80%',
    '::after,::before': {
      content: '""',
      position: 'absolute',
      zindex: 10,
      backgroundColor: '$textDeEmphasized'
    },
    '::before': {
      width: '100%',
      height: '1px'
    },
    '::after': {
      height: '100%',
      width: '1px'
    }
  },
  '> span': {
    position: 'relative',
    zindex: 100,
    width: '10px',
    height: '10px',
    backgroundColor: '$accent',
    borderRadius: '50%'
  }
});

function Joystick({
  value,
  settings,
  onUpdate
}) {
  const timeout = React.useRef();
  const outOfBoundsX = React.useRef(0);
  const outOfBoundsY = React.useRef(0);
  const [showJoystick, setShowJoystick] = React.useState(false);
  const [isOutOfBounds, setIsOutOfBounds] = React.useState(false);
  const [spanRef, set] = useTransform();
  const {
    x: {
      step: stepX
    },
    y: {
      step: stepY
    }
  } = settings;
  const wpx = Vector.useTh('sizes', '$joystickWidth');
  const hpx = Vector.useTh('sizes', '$joystickHeight');
  const w = parseFloat(wpx) * 0.8 / 2;
  const h = parseFloat(hpx) * 0.8 / 2;
  const startOutOfBounds = React.useCallback(() => {
    if (timeout.current) return;
    setIsOutOfBounds(true);
    if (outOfBoundsX.current) set({
      x: outOfBoundsX.current * w
    });
    if (outOfBoundsY.current) set({
      y: outOfBoundsY.current * -h
    });
    timeout.current = window.setInterval(() => {
      onUpdate(v => {
        const incX = stepX * outOfBoundsX.current * 3;
        const incY = stepY * outOfBoundsY.current * 3;
        return Array.isArray(v) ? [v[0] + incX, v[1] + incY] : {
          x: v.x + incX,
          y: v.y + incY
        };
      });
    }, 16);
  }, [w, h, onUpdate, set, stepX, stepY]);
  const endOutOfBounds = React.useCallback(() => {
    window.clearTimeout(timeout.current);
    timeout.current = undefined;
    setIsOutOfBounds(false);
  }, []);
  React.useEffect(() => () => window.clearTimeout(timeout.current), []);
  const bind = reactUseGesture.useDrag(({
    active,
    delta: [dx, dy],
    movement: [mx, my]
  }) => {
    setShowJoystick(active);

    const _x = vectorPlugin.clamp(mx, -w, w);

    const _y = vectorPlugin.clamp(my, -h, h);

    outOfBoundsX.current = Math.abs(mx) > Math.abs(_x) ? Math.sign(mx - _x) : 0;
    outOfBoundsY.current = Math.abs(my) > Math.abs(_y) ? Math.sign(_y - my) : 0;
    let newX = value.x;
    let newY = value.y;

    if (active) {
      if (!outOfBoundsX.current) {
        newX += dx * stepX;
        set({
          x: _x
        });
      }

      if (!outOfBoundsY.current) {
        newY -= dy * stepY;
        set({
          y: _y
        });
      }

      if (outOfBoundsX.current || outOfBoundsY.current) startOutOfBounds();else endOutOfBounds();
      onUpdate({
        x: newX,
        y: newY
      });
    } else {
      outOfBoundsX.current = 0;
      outOfBoundsY.current = 0;
      set({
        x: 0,
        y: 0
      });
      endOutOfBounds();
    }
  });
  return /*#__PURE__*/React__default['default'].createElement(JoystickTrigger, bind(), showJoystick && /*#__PURE__*/React__default['default'].createElement(JoystickPlayground, {
    isOutOfBounds: isOutOfBounds
  }, /*#__PURE__*/React__default['default'].createElement("div", null), /*#__PURE__*/React__default['default'].createElement("span", {
    ref: spanRef
  })));
}

const Container$1 = Vector.styled('div', {
  display: 'grid',
  gridTemplateColumns: 'var(--sizes-rowHeight) repeat(2, 1fr)',
  gridColumnGap: '$colGap'
});
function Point2d() {
  const {
    label,
    displayValue,
    onUpdate,
    settings
  } = Vector.useInputContext();
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(Container$1, null, /*#__PURE__*/React__default['default'].createElement(Joystick, {
    value: displayValue,
    settings: settings,
    onUpdate: onUpdate
  }), /*#__PURE__*/React__default['default'].createElement(Vector.Vector, {
    value: displayValue,
    settings: settings,
    onUpdate: onUpdate
  })));
}

const KEYS$1 = ['x', 'y'];

const plugin$6 = { ...vectorPlugin.getVectorPlugin(KEYS$1),
  component: Point2d
};

const sanitize$1 = v => {
  if (!v) return undefined;

  try {
    return URL.createObjectURL(v);
  } catch (e) {
    return undefined;
  }
};
const schema$4 = (_o, s) => typeof s === 'object' && 'image' in s;
const normalize$2 = ({
  image
}) => {
  return {
    value: image
  };
};

var props$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  sanitize: sanitize$1,
  schema: schema$4,
  normalize: normalize$2
});

const DropZone = Vector.styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  overflow: 'hidden',
  minHeight: '$rowHeight',
  background: '$elevation3',
  color: '$text',
  borderRadius: '$sm',
  transition: '$borderBg',
  outline: 'none',
  cursor: 'pointer',
  '&:hover': {},
  variants: {
    isDragAccept: {
      true: {}
    }
  }
});
const Preview = Vector.styled('div', {
  position: 'relative',
  width: '100%',
  '> img': {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  }
});
const Instructions = Vector.styled('div', {
  fontSize: '0.8em',
  height: '100%',
  padding: '$rowV $rowH'
});
const Remove = Vector.styled('div', {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  top: '0',
  right: '0',
  margin: '$rowV $rowH',
  height: '20px',
  width: '20px',
  borderRadius: '$sm',
  backgroundColor: '$elevation3',
  transition: '$borderBg',
  ':hover': {
    backgroundColor: '$accent'
  },
  '::after,::before': {
    content: '""',
    position: 'absolute',
    height: '2px',
    width: '10px',
    borderRadius: '1px',
    backgroundColor: '$textEmphasized'
  },
  '::after': {
    transform: 'rotate(45deg)'
  },
  '::before': {
    transform: 'rotate(-45deg)'
  }
});

function Image() {
  const {
    label,
    value,
    onUpdate
  } = Vector.useInputContext();
  const onDrop = React.useCallback(acceptedFiles => {
    if (!acceptedFiles.length) return;
    onUpdate(acceptedFiles[0]);
  }, [onUpdate]);
  const clear = React.useCallback(e => {
    e.stopPropagation();
    onUpdate(undefined);
  }, [onUpdate]);
  const {
    getRootProps,
    getInputProps,
    isDragAccept
  } = reactDropzone.useDropzone({
    maxFiles: 1,
    accept: 'image/*',
    onDrop
  });
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(DropZone, getRootProps({
    isDragAccept
  }), /*#__PURE__*/React__default['default'].createElement("input", getInputProps()), value ? /*#__PURE__*/React__default['default'].createElement(Preview, null, /*#__PURE__*/React__default['default'].createElement("img", {
    src: value,
    alt: `preview for ${label}`
  }), /*#__PURE__*/React__default['default'].createElement(Remove, {
    onClick: clear
  })) : /*#__PURE__*/React__default['default'].createElement(Instructions, null, isDragAccept ? 'drop image here' : 'click to select')));
}

const plugin$7 = { ...props$4,
  component: Image
};

const number = v8n__default['default']().number();
const schema$5 = (o, s) => v8n__default['default']().array().length(2).every.number().test(o) && v8n__default['default']().schema({
  min: number,
  max: number
}).test(s);
const format$2 = v => ({
  min: v[0],
  max: v[1]
});
const sanitize$2 = ({
  min,
  max
}, {
  bounds: [MIN, MAX]
}) => [vectorPlugin.clamp(Number(min), MIN, Math.max(MIN, max)), vectorPlugin.clamp(Number(max), Math.min(MAX, min), MAX)];
const normalize$3 = ({
  value,
  min,
  max
}) => {
  const boundsSettings = {
    min,
    max
  };
  const {
    settings
  } = vectorPlugin.normalizeKeyedNumberInput(format$2(value), {
    min: boundsSettings,
    max: boundsSettings
  });
  const bounds = [min, max];
  return {
    value,
    settings: { ...settings,
      bounds
    }
  };
};

var props$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  schema: schema$5,
  format: format$2,
  sanitize: sanitize$2,
  normalize: normalize$3
});

const Container$2 = Vector.styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridColumnGap: '$colGap'
});

function IntervalSlider({
  value,
  bounds: [min, max],
  onDrag,
  ...settings
}) {
  const ref = React.useRef(null);
  const minScrubberRef = React.useRef(null);
  const maxScrubberRef = React.useRef(null);
  const rangeWidth = React.useRef(0);
  const scrubberWidth = Vector.useTh('sizes', '$scrubberWidth');
  const bind = Vector.useDrag(({
    event,
    first,
    xy: [x],
    movement: [mx],
    memo = {}
  }) => {
    if (first) {
      const {
        width,
        left
      } = ref.current.getBoundingClientRect();
      rangeWidth.current = width - parseFloat(scrubberWidth);
      const targetIsScrub = event?.target === minScrubberRef.current || event?.target === maxScrubberRef.current;
      memo.pos = vectorPlugin.invertedRange((x - left) / width, min, max);
      const delta = Math.abs(memo.pos - value.min) - Math.abs(memo.pos - value.max);
      memo.key = delta < 0 || delta === 0 && memo.pos <= value.min ? 'min' : 'max';
      if (targetIsScrub) memo.pos = value[memo.key];
    }

    const newValue = memo.pos + vectorPlugin.invertedRange(mx / rangeWidth.current, 0, max - min);
    onDrag({ ...value,
      [memo.key]: sanitizeStep(newValue, settings[memo.key])
    });
    return memo;
  });
  const minStyle = `calc(${vectorPlugin.range(value.min, min, max)} * (100% - ${scrubberWidth}))`;
  const maxStyle = `calc(${1 - vectorPlugin.range(value.max, min, max)} * (100% - ${scrubberWidth}))`;
  return /*#__PURE__*/React__default['default'].createElement(Vector.RangeWrapper, Vector._extends({
    ref: ref
  }, bind()), /*#__PURE__*/React__default['default'].createElement(Vector.Range, null, /*#__PURE__*/React__default['default'].createElement(Vector.Indicator, {
    style: {
      left: minStyle,
      right: maxStyle
    }
  })), /*#__PURE__*/React__default['default'].createElement(Vector.Scrubber, {
    position: "left",
    ref: minScrubberRef,
    style: {
      left: minStyle
    }
  }), /*#__PURE__*/React__default['default'].createElement(Vector.Scrubber, {
    position: "right",
    ref: maxScrubberRef,
    style: {
      right: maxStyle
    }
  }));
}

function Interval() {
  const {
    label,
    displayValue,
    onUpdate,
    settings
  } = Vector.useInputContext();
  const {
    bounds,
    ..._settings
  } = settings;
  return /*#__PURE__*/React__default['default'].createElement(Vector.Row, {
    input: true
  }, /*#__PURE__*/React__default['default'].createElement(Vector.Label, null, label), /*#__PURE__*/React__default['default'].createElement(Vector.Row, null, /*#__PURE__*/React__default['default'].createElement(Vector.Row, null, /*#__PURE__*/React__default['default'].createElement(IntervalSlider, Vector._extends({
    value: displayValue
  }, settings, {
    onDrag: onUpdate
  }))), /*#__PURE__*/React__default['default'].createElement(Container$2, null, /*#__PURE__*/React__default['default'].createElement(Vector.Vector, {
    value: displayValue,
    settings: _settings,
    onUpdate: onUpdate
  }))));
}

const plugin$8 = { ...props$5,
  component: Interval
};

Vector.register('SELECT', plugin$1);
Vector.register('IMAGE', plugin$7);
Vector.register('NUMBER', plugin);
Vector.register('COLOR', plugin$2);
Vector.register('STRING', plugin$3);
Vector.register('BOOLEAN', plugin$4);
Vector.register('INTERVAL', plugin$8);
Vector.register('POINT3D', plugin$5);
Vector.register('POINT2D', plugin$6);
function useControls(nameOrSchema, schema, settings) {
  const _name = typeof nameOrSchema === 'string' ? nameOrSchema : undefined;

  const _schema = React.useRef(_name ? {
    [_name]: folder(schema, settings)
  } : nameOrSchema);

  const initialData = React.useMemo(() => getDataFromSchema(_schema.current), []);
  const paths = React.useMemo(() => getPaths(initialData), [initialData]);
  const values = useValuesForPath(paths, initialData);
  React.useEffect(() => {
    // we need to compute these in useEffect for monitors to work
    // but this breaks the order of keys
    store.setData(initialData);
    return () => store.disposePaths(paths);
  }, [paths, initialData]); // renders <Leva /> only if it's not manually rendered by the user

  useRenderRoot();
  return values;
}

/**
 *
 * @param name button name
 * @param onClick function that executes when the button is clicked
 */

function button(onClick) {
  return {
    type: vectorPlugin.SpecialInputTypes.BUTTON,
    onClick
  };
}

const defaultSettings$1 = {
  graph: false,
  interval: 100
};
function monitor(objectOrFn, settings) {
  return {
    type: vectorPlugin.SpecialInputTypes.MONITOR,
    objectOrFn,
    settings: { ...defaultSettings$1,
      ...settings
    }
  };
}

exports.Leva = Leva;
exports.button = button;
exports.folder = folder;
exports.monitor = monitor;
exports.store = store;
exports.useControls = useControls;
